/**
 * The multi-turn, tool-using shape of a Claude request.
 *
 * `generateWithClaude` is a one-shot: one system prompt, one user turn, one
 * text answer. That is the right shape for the twenty-odd callers that have it,
 * and the wrong shape for an agent that has to look before it speaks. A
 * reasoning episode is a CONVERSATION with itself: ask, call a tool, read the
 * result, decide whether that changed the answer, ask again. Each iteration is
 * its own HTTP request (the API is stateless), so the whole history travels
 * every time and the client owns the loop.
 *
 * The types here are deliberately the wire shapes rather than a friendlier
 * abstraction, for one reason that is not negotiable: **an assistant turn has
 * to be echoed back byte for byte.** Extended-thinking blocks carry a
 * `signature` the API validates on the next request, and a tool_use block
 * carries an `id` the matching tool_result has to name. A DTO that
 * re-serialises "the interesting fields" drops whichever field is added next
 * and fails at runtime, in production, on the turn that matters. So blocks are
 * carried as open records and passed through untouched.
 */

import type { AiContentBlock } from './documentIntake'

// ============================================================
// Blocks
// ============================================================

/**
 * One content block, on the wire.
 *
 * Open on purpose - see the module comment. Read it with the accessors below
 * rather than casting at each call site.
 */
export type AiBlock = { type: string } & Record<string, unknown>

/** A tool result going back to the model. One per `tool_use` in the turn it answers. */
export interface AiToolResult {
  type: 'tool_result'
  tool_use_id: string
  content: string
  /**
   * Set when the tool failed.
   *
   * A failed tool still gets a result block. Dropping it leaves a `tool_use`
   * with no answer, which is a malformed conversation the API rejects, and
   * telling the model nothing about a failure it caused is the surest way to
   * have it repeat the call.
   */
  is_error?: boolean
}

/** What one turn's `content` may hold on the way out. */
export type AiRequestBlock = AiContentBlock | AiToolResult | AiBlock

export interface AiConversationMessage {
  role: 'user' | 'assistant'
  content: string | readonly AiRequestBlock[]
}

/** A `tool_use` block, read out of an assistant turn. */
export interface AiToolUse {
  id: string
  name: string
  input: Record<string, unknown>
}

/** Pull every `tool_use` out of an assistant turn, in the order the model emitted them. */
export function readToolUses(content: readonly AiBlock[]): AiToolUse[] {
  const uses: AiToolUse[] = []
  for (const block of content) {
    if (block.type !== 'tool_use') continue
    const id = typeof block.id === 'string' ? block.id : null
    const name = typeof block.name === 'string' ? block.name : null
    if (!id || !name) continue
    const input = block.input && typeof block.input === 'object' && !Array.isArray(block.input)
      ? block.input as Record<string, unknown>
      : {}
    uses.push({ id, name, input })
  }
  return uses
}

/** Join every text block in a turn. Same convention `generateWithClaude` uses. */
export function readText(content: readonly AiBlock[]): string {
  return content
    .filter(b => b.type === 'text' && typeof b.text === 'string')
    .map(b => b.text as string)
    .join('\n')
}

/**
 * The thinking a turn produced, as text.
 *
 * Empty when the model is configured with `display: "omitted"`, which is the
 * default on several current models - that is not an error, it just means there
 * is nothing readable to show. Never treat an empty string as "it did not
 * think": thinking happens and is billed under every display setting.
 */
export function readThinking(content: readonly AiBlock[]): string {
  return content
    .filter(b => b.type === 'thinking' && typeof b.thinking === 'string')
    .map(b => (b.thinking as string).trim())
    .filter(Boolean)
    .join('\n')
}

// ============================================================
// Tools
// ============================================================

export interface AiToolSpec {
  name: string
  description: string
  input_schema: {
    type: 'object'
    properties: Record<string, unknown>
    required?: readonly string[]
  }
}

// ============================================================
// Extended thinking
// ============================================================

export type AiThinkingConfig =
  | { type: 'adaptive'; display?: 'summarized' | 'omitted' }
  | { type: 'enabled'; budget_tokens: number }

/**
 * Models that take `thinking: {type: "adaptive"}`.
 *
 * The 4.6 generation and everything after it. `budget_tokens` is deprecated on
 * 4.6 and REJECTED WITH A 400 on 4.7 and later, so the two shapes are not
 * interchangeable and the split has to be made from the model id.
 */
const ADAPTIVE_THINKING = /^claude-(?:fable|mythos|opus|sonnet)-(?:5|4-6|4-7|4-8)(?:$|-)/

/**
 * Models that still take a fixed thinking budget.
 *
 * The 4.5 generation and 3.7 Sonnet. Nothing in this app configures one, but
 * `VITE_AI_MODEL` is an environment variable and a deployment can point it
 * anywhere.
 */
const BUDGETED_THINKING = /^claude-(?:opus|sonnet|haiku)-4-5(?:$|-)|^claude-3-7-sonnet(?:$|-)/

/**
 * The `thinking` parameter for a model, or null when it takes none.
 *
 * Null rather than a guess is the important half. Sending the wrong shape is a
 * 400 that kills the whole turn, and an unrecognised model id - a new release,
 * a typo in the environment, a provider-prefixed Bedrock id - is exactly the
 * case where guessing costs more than going without. The agent reasons fine
 * without extended thinking; it does not reason at all through a 400.
 *
 * `display: "summarized"` is explicit rather than left to the default because
 * the default is not the same on every model in the adaptive family: it is
 * "summarized" on the 4.6 generation and "omitted" from Sonnet 5 / Opus 5
 * onwards. The panel shows what the agent is thinking, so the setting has to be
 * stated rather than inherited.
 */
/**
 * Effort levels every model in `ADAPTIVE_THINKING` accepts.
 *
 * Deliberately excludes `xhigh`, which only arrived with Opus 4.7 and is a 400
 * on the 4.6 generation this app is configured for. `high` is the API default,
 * so it is here to be explicit rather than because it needs sending.
 */
export type AiEffort = 'low' | 'medium' | 'high' | 'max'

/**
 * The `output_config.effort` value for a model, or null when it takes none.
 *
 * Same rule as `thinkingConfigFor` and for the same reason: an unrecognised
 * model id is exactly where guessing costs more than going without, and effort
 * on a model that does not take it is a 400 that kills the whole turn. The 4.5
 * generation is left out on purpose - `effort` errors on Sonnet 4.5 and Haiku
 * 4.5, and going without it there just means the default.
 */
export function effortConfigFor(model: string, effort?: AiEffort): AiEffort | null {
  if (!effort) return null
  return ADAPTIVE_THINKING.test(model.trim().toLowerCase()) ? effort : null
}

export function thinkingConfigFor(
  model: string,
  opts: { budgetTokens?: number } = {},
): AiThinkingConfig | null {
  const id = model.trim().toLowerCase()
  if (ADAPTIVE_THINKING.test(id)) return { type: 'adaptive', display: 'summarized' }
  if (BUDGETED_THINKING.test(id)) {
    // The API floor is 1024 and the budget must stay under `max_tokens`; the
    // caller passes the room it actually has.
    const budget = Math.max(1024, Math.floor(opts.budgetTokens ?? 4000))
    return { type: 'enabled', budget_tokens: budget }
  }
  return null
}

// ============================================================
// One request, one response
// ============================================================

export interface AiTurnRequest {
  system: string
  messages: readonly AiConversationMessage[]
  maxTokens: number
  /** Advertised tools. Omitted entirely when empty, so a plain turn is a plain turn. */
  tools?: readonly AiToolSpec[]
  /** Overrides the configured model. */
  model?: string
  /**
   * `null` suppresses extended thinking; omitting it derives the right shape
   * from the model. Pass a config only to override the derivation.
   */
  thinking?: AiThinkingConfig | null
  /**
   * Cache the tools + system prefix.
   *
   * Worth it whenever the same system prompt will be sent more than once, which
   * is every iteration of a reasoning episode after the first. The prefix here
   * is ~26k tokens; re-sending it eight times uncached is the dominant cost of
   * an episode, and it is the one cost that buys nothing.
   */
  cacheSystem?: boolean
  /**
   * How hard the model should work on this one call.
   *
   * Omitted means the API default, which is `high`. Worth setting DOWN on a
   * call that is transcription rather than reasoning: the write-up at the end
   * of a reasoning episode has already done its thinking across the rounds
   * before it, and paying `high` again to restate the findings is where a turn
   * quietly loses a minute and a half it does not have.
   *
   * Dropped for a model that does not take it - see `effortConfigFor`.
   */
  effort?: AiEffort
  signal?: AbortSignal
}

export interface AiTurnUsage {
  inputTokens: number
  outputTokens: number
  cacheReadInputTokens: number
  cacheCreationInputTokens: number
}

export interface AiTurnResponse {
  /** The assistant turn, verbatim. Echo it back unchanged on the next request. */
  content: AiBlock[]
  /** `end_turn`, `tool_use`, `max_tokens`, `refusal`, ... */
  stopReason: string | null
  /** What the server actually ran. Not necessarily what was asked for. */
  model: string
  usage: AiTurnUsage
}

/** Every token the turn touched, cached or not. What a budget should count. */
export function totalTokens(usage: AiTurnUsage): number {
  return usage.inputTokens + usage.outputTokens
    + usage.cacheReadInputTokens + usage.cacheCreationInputTokens
}

export const EMPTY_USAGE: AiTurnUsage = {
  inputTokens: 0,
  outputTokens: 0,
  cacheReadInputTokens: 0,
  cacheCreationInputTokens: 0,
}

/** Read the usage block off a response, tolerating every field being absent. */
export function readUsage(raw: unknown): AiTurnUsage {
  const u = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
  return {
    inputTokens: n(u.input_tokens),
    outputTokens: n(u.output_tokens),
    cacheReadInputTokens: n(u.cache_read_input_tokens),
    cacheCreationInputTokens: n(u.cache_creation_input_tokens),
  }
}

/**
 * The transport a reasoning loop runs on.
 *
 * `generateWithClaudeMessages` satisfies it. A test passes a recorder, which is
 * how the loop's bounds and its batching rule are asserted without a network.
 */
export type ConverseFn = (req: AiTurnRequest) => Promise<AiTurnResponse>
